/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { Case, Switch } from "react-if";
import { ChartType, EstateV2Filter } from "~/generated/graphql";
import { DateRange, EstatePeriod, Granularity } from "~/components";
import { useQueryState } from "~/hooks";
import { showError } from "~/services/toast";
import { EstateRecordsAccumulatedCost } from "./EstateRecordsAccumulatedCost";
import { EstateRecordsCostPerApplication } from "./EstateRecordsCostPerApplication";
import { EstateRecordsCostPerOrgUnit } from "./EstateRecordsCostPerOrgUnit";
import { EstateRecordsEnvironmentType } from "./EstateRecordsEnvironmentType";
import { getEndDate, getStartDate } from "./helpers";
import { useEstateRecordsChartJob } from "./hooks";
import { EstateChartsQueryState, EstateGroupBy } from "./types";

type Props = {
  granularity: Granularity;
  period: EstatePeriod;
  activeChart: EstateGroupBy;
  estateFilters: { filter: EstateV2Filter };
  range: DateRange | undefined;
  isCostLoading: boolean;
  currentMonthTotal: string | undefined;
  totalCost?: string;
  estateRecordsCount?: number | null;
  monthlySavings?: string | null;
};

export function EstateRecordsChart({
  granularity,
  period,
  activeChart,
  estateFilters,
  range,
  isCostLoading,
  currentMonthTotal,
  estateRecordsCount,
  monthlySavings,
}: Props) {
  const {
    state: { periodStart, periodEnd },
  } = useQueryState<EstateChartsQueryState>();

  const startDate =
    periodStart ?? getStartDate(EstatePeriod.CURRENT_MONTH, undefined);
  const endDate =
    periodEnd ?? getEndDate(EstatePeriod.CURRENT_MONTH, undefined);

  const { chartData, loading, error } = useEstateRecordsChartJob(
    {
      variables: {
        ...estateFilters,
        startDate,
        endDate,
        chartType: mapChartTypes(activeChart),
      },
    },
    showError
  );

  function isChartVisible(chart: EstateGroupBy) {
    return chart === activeChart;
  }

  return (
    <Switch>
      <Case condition={isChartVisible(EstateGroupBy.NONE)}>
        <EstateRecordsAccumulatedCost
          startDate={startDate}
          endDate={endDate}
          granularity={granularity}
          period={period}
          range={range}
          loading={loading}
          isCostLoading={isCostLoading}
          chartData={chartData}
          error={error}
          currentMonthTotal={currentMonthTotal}
          monthlySavings={monthlySavings}
          estateRecordsCount={estateRecordsCount ?? 0}
        />
      </Case>
      <Case condition={isChartVisible(EstateGroupBy.APP)}>
        <EstateRecordsCostPerApplication
          granularity={granularity}
          startDate={startDate}
          endDate={endDate}
          period={period}
          chartData={chartData}
          loading={loading}
          error={error}
          range={range}
          isCostLoading={isCostLoading}
          monthlySavings={monthlySavings}
        />
      </Case>
      <Case condition={isChartVisible(EstateGroupBy.ORG)}>
        <EstateRecordsCostPerOrgUnit
          granularity={granularity}
          startDate={startDate}
          endDate={endDate}
          period={period}
        />
      </Case>
      <Case condition={isChartVisible(EstateGroupBy.ENV)}>
        <EstateRecordsEnvironmentType
          granularity={granularity}
          startDate={startDate}
          endDate={endDate}
          period={period}
        />
      </Case>
    </Switch>
  );
}

// map the rest of chart types here when implemented
function mapChartTypes(activeChart: EstateGroupBy) {
  switch (activeChart) {
    case EstateGroupBy.APP:
      return ChartType.Application;
    case EstateGroupBy.ENV:
      return ChartType.EnvironmentType;
    case EstateGroupBy.ORG:
      return ChartType.OrgUnit;
    default:
      return ChartType.Accumulated;
  }
}
